/**
 * Shared shapes for the free website audit.
 *
 * THIS FILE IS DELIBERATELY FREE OF NODE IMPORTS. The audit runner needs `dns`
 * and `net` to refuse private addresses, and the results are rendered by a
 * client component. Importing the runner from the client, even for a type,
 * dragged `node:dns/promises` into the browser bundle and failed the build. So
 * the types and the labels live here, where both sides can reach them, and the
 * runner keeps the parts that only work on a server.
 */

export type CheckStatus = "pass" | "warn" | "fail" | "info";

export type AuditGroupKey =
  | "speed"
  | "found"
  | "legal"
  | "security"
  | "enquiries"
  | "mobile";

export type AuditCheck = {
  id: string;
  /** The heading a business owner reads. Never jargon. */
  title: string;
  status: CheckStatus;
  /** What we found, in a sentence they can act on. */
  detail: string;
  group: AuditGroupKey;
};

export type AuditResult = {
  url: string;
  finalUrl: string;
  fetchedAt: string;
  checks: AuditCheck[];
  counts: { pass: number; warn: number; fail: number };
  /** Plain-English headline for the email subject and the top of the report. */
  headline: string;
};

/**
 * How many checks the report actually runs. Twenty four, and the copy on /free/website-audit
 * saying "twenty four checks" is correct.
 *
 * TYPED HERE AND GUARDED BY A TEST, because it cannot be derived: `analysePage` builds the
 * list at runtime from the fetched HTML, so nothing static can count it. Counting the literal
 * check ids in `site-audit.ts` gives twenty and is WRONG: four more are pushed from shared
 * helpers rather than written out, which is exactly the trap this constant closes. The number
 * now appears in one place, `site-audit.test.ts` asserts the real count against it, and the
 * build says so when a check is added or removed instead of the figure quietly going stale on
 * every page that quotes it.
 */
export const AUDIT_CHECK_COUNT = 24;

export const AUDIT_GROUPS: Record<AuditGroupKey, { name: string; blurb: string }> = {
  speed: { name: "Speed", blurb: "How long people wait before they see anything." },
  found: { name: "Getting found", blurb: "What Google needs before it can rank you." },
  legal: { name: "Legal cover", blurb: "What UK law expects on a business website." },
  security: {
    name: "Security",
    blurb: "The headers that stop other people abusing your site.",
  },
  enquiries: {
    name: "Turning visits into enquiries",
    blurb: "Whether anyone can actually contact you.",
  },
  mobile: {
    name: "Phones and accessibility",
    blurb: "Most of your visitors are holding a phone.",
  },
};
